// ./src/utils/inventoryScanLine.ts
//
// What an inventory item brings to the operation line when it is chosen — ONE copy, used by every
// way the ops console picks an item: a scan, an item created from an unknown scan, and a manual pick
// (ROADMAP #65 a, 2026-09-28).
//
// ⚠⚠ WHY IT EXISTS. The three paths each set PART of the line, and none set all of it: a created
// item kept only its name and currency (pack size reset to 1), and NO path ever applied the item's
// default vendor. The owner's run: scan an unknown code → create it with a vendor, a label and
// 24/pack → the Receive form showed none of it, and leaving and re-entering the page still lost
// the vendor. Every path now calls `lineFromItem`, so a fourth one cannot half-implement it again.

// Type-only: this module must stay free of runtime imports so `scripts/checkInventoryScan.ts` can
// RUN it rather than grep it.
import type { InventoryItem, InventoryBarcode } from "./api/InventoryItemService";

/** The barcode of `item` that `scannedCode` is, or null (a manual pick, or a code it does not own). */
export const matchScannedBarcode = (item: Pick<InventoryItem, "barcodes">, scannedCode: string | null | undefined): InventoryBarcode | null => {
  const code = (scannedCode ?? "").trim();

  if (!code) {
    return null;
  }

  return (item.barcodes ?? []).find((b) => (b.code ?? "").trim() === code) ?? null;
};

/** What a chosen item sets on the line. */
export interface ScanLine {
  /** Stock units one entered quantity stands for — the scanned barcode's pack size, else 1. */
  packUnits: number;
  /** The scanned barcode's own label ("Case of 24"), so the host can see WHICH pack they scanned. */
  packLabel: string | null;
  /** True when the scanned code belongs to this item — false means the line is in plain units. */
  codeMatched: boolean;
}

export const lineFromItem = (item: Pick<InventoryItem, "barcodes">, scannedCode: string | null | undefined): ScanLine => {
  const matched = matchScannedBarcode(item, scannedCode);
  const perCode = Math.floor(Number(matched?.unitsPerCode));

  return {
    packUnits: Number.isFinite(perCode) && perCode > 1 ? perCode : 1,
    packLabel: (matched?.label ?? "").trim() || null,
    codeMatched: !!matched,
  };
};

/**
 * Which vendor a line is recorded against.
 *
 * ⚠ A vendor the operator PICKED this session wins — it is the supplier of the delivery in front of
 * them, and a pallet from one supplier can hold items whose catalog default is somebody else. Only
 * when nobody has picked one does the item's default vendor apply; and an item with no default gets
 * NO vendor rather than the previous item's, which would credit the wrong supplier.
 */
export const lineVendor = (pinnedVendorId: string | null | undefined, item: Pick<InventoryItem, "defaultVendorId"> | null | undefined): string | null =>
  pinnedVendorId || item?.defaultVendorId || null;

/**
 * A typed units-per-pack value, or null while it is not one yet (empty, 0, "abc").
 *
 * ⚠⚠ #65 f: the field used to coerce every such value to 1 ON EACH KEYSTROKE, so clearing it to
 * type "24" left "1", then "12", then "124". Null here means "not a value yet" — the editor keeps
 * the draft as typed and only a real value reaches the row.
 */
export const parseUnitsPerCode = (value: number | string | null | undefined): number | null => {
  if (value === "" || value === null || value === undefined) {
    return null;
  }

  const n = Math.floor(Number(value));

  return Number.isFinite(n) && n >= 1 ? n : null;
};
